// RecommendedCard.tsx — compact icon card for the Recommended-for-You grid.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ConsolePanel, Button } from '@/ui';
import { useCommunities, useRequireAuth } from '@/hooks';
import { CommunityIcon } from './CommunityIcon';
import type { Community } from '@/core/models/community';

const Card = styled(ConsolePanel)`display: flex; flex-direction: column; gap: 8px;`;
const Head = styled.div`display: flex; align-items: center; gap: 10px;`;
const Info = styled.div`min-width: 0; display: flex; flex-direction: column;`;
const Name = styled(Link)`
  font-family: ${({ theme }) => theme.font.body}; font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;
const Cat = styled.span`
  font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Desc = styled.p`
  margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
const Actions = styled.div`display: flex; justify-content: flex-end;`;

export function RecommendedCard({ community }: { community: Community }) {
  const { toggleJoin } = useCommunities();
  const requireAuth = useRequireAuth();
  function handleJoin() {
    if (!requireAuth(`join ${community.name}`)) return;
    void toggleJoin(community.id);
  }
  return (
    <Card>
      <Head>
        <CommunityIcon svg={community.icon} size={40} />
        <Info>
          <Name to={`/communities/${community.id}`}>{community.name}</Name>
          <Cat>{community.category}</Cat>
        </Info>
      </Head>
      <Desc>{community.description}</Desc>
      <Actions>
        <Button
          variant={community.joined ? 'secondary' : 'primary'}
          size="sm"
          onClick={handleJoin}
          aria-label={`${community.joined ? 'Leave' : 'Join'} ${community.name}`}
        >
          {community.joined ? 'Joined' : 'Join'}
        </Button>
      </Actions>
    </Card>
  );
}
